import { useState, useRef, useEffect } from 'react'
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Divider,
    Chip,
    Card,
    CardContent,
    Grid,
    IconButton
} from '@mui/material'
import {
    QrCodeScanner as QrIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    CameraAlt as CameraIcon
} from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useAuthStore } from '@store/authStore'
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const Scanner = () => {
    const { t, language } = useTranslation()
    const { user, token } = useAuthStore()
    const isRTL = language === 'ar'

    const [verificationCode, setVerificationCode] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [result, setResult] = useState(null)
    const [cameraActive, setCameraActive] = useState(false)
    const [cameraLoading, setCameraLoading] = useState(false)
    const [cameraError, setCameraError] = useState(null)
    const [scanStatus, setScanStatus] = useState('')

    const videoRef = useRef(null)
    const canvasRef = useRef(null)
    const streamRef = useRef(null)
    const isScanningRef = useRef(false)

    // Cleanup camera on unmount
    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop())
            }
        }
    }, [])

    const startCamera = async () => {
        console.log('Starting camera...')
        try {
            setCameraLoading(true)
            setCameraError(null)
            setCameraActive(false)

            // Stop any existing stream first
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop())
            }

            // Request camera with fallback constraints
            let stream
            try {
                // Try with back camera first
                stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: { ideal: 'environment' },
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                    }
                })
            } catch (e) {
                console.log('Back camera failed, trying any camera:', e)
                // Fallback to any camera
                stream = await navigator.mediaDevices.getUserMedia({
                    video: true
                })
            }

            console.log('Camera stream obtained:', stream)
            streamRef.current = stream

            if (videoRef.current) {
                videoRef.current.srcObject = stream

                // Set camera active immediately so the video element shows
                setCameraActive(true)
                isScanningRef.current = true
                setScanStatus('Initializing scanner...')

                // Start scanning when video actually starts playing
                videoRef.current.onplay = () => {
                    console.log('Video onplay event fired')
                    setScanStatus('Video playing, starting scan...')
                    setCameraLoading(false)
                    // Start the scanning loop
                    setTimeout(() => {
                        console.log('Starting scan loop, isScanningRef:', isScanningRef.current)
                        scanQRCode()
                    }, 100)
                }

                // Try to play the video
                videoRef.current.play().catch(err => {
                    console.error('Play failed:', err)
                    setScanStatus('Play failed, retrying...')
                    setCameraLoading(false)
                })
            } else {
                console.error('videoRef.current is null')
                setScanStatus('Video element not found')
                setCameraLoading(false)
            }
        } catch (err) {
            console.error('Camera error:', err)
            let errorMessage = language === 'ar'
                ? 'فشل الوصول إلى الكاميرا. يرجى التحقق من الأذونات.'
                : 'Failed to access camera. Please check permissions.'

            if (err.name === 'NotAllowedError') {
                errorMessage = language === 'ar'
                    ? 'تم رفض الوصول إلى الكاميرا. يرجى السماح بالوصول إلى الكاميرا في إعدادات المتصفح.'
                    : 'Camera access denied. Please allow camera access in browser settings.'
            } else if (err.name === 'NotFoundError') {
                errorMessage = language === 'ar'
                    ? 'لم يتم العثور على كاميرا.'
                    : 'No camera found.'
            } else if (err.name === 'NotReadableError') {
                errorMessage = language === 'ar'
                    ? 'الكاميرا قيد الاستخدام من قبل تطبيق آخر.'
                    : 'Camera is already in use by another application.'
            }

            setCameraError(errorMessage)
        } finally {
            setCameraLoading(false)
        }
    }

    const stopCamera = () => {
        isScanningRef.current = false
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop())
            streamRef.current = null
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null
        }
        setCameraActive(false)
        setScanStatus('')
    }

    const scanQRCode = () => {
        if (!videoRef.current || !canvasRef.current || !isScanningRef.current) {
            setScanStatus(`Stopped (video:${!!videoRef.current}, canvas:${!!canvasRef.current}, scanning:${isScanningRef.current})`)
            return
        }

        const video = videoRef.current
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')

        if (video.readyState === video.HAVE_ENOUGH_DATA) {
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight

            if (canvas.width > 0 && canvas.height > 0) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)

                // Use jsQR library if available
                if (window.jsQR) {
                    setScanStatus(`Scanning... (${canvas.width}x${canvas.height})`)

                    // Try with different inversion attempts for better detection
                    let code = window.jsQR(imageData.data, imageData.width, imageData.height, {
                        inversionAttempts: 'attemptBoth'
                    })

                    if (code) {
                        setScanStatus('QR Detected!')
                        setVerificationCode(code.data)
                        stopCamera()
                        handleVerify(code.data)
                        return
                    }
                } else {
                    setScanStatus('⚠️ jsQR library NOT loaded!')
                }
            } else {
                setScanStatus('Waiting for video...')
            }
        } else {
            setScanStatus('Video not ready')
        }

        // Continue scanning
        if (isScanningRef.current) {
            requestAnimationFrame(scanQRCode)
        }
    }

    const handleVerify = async (code = verificationCode) => {
        if (!code.trim()) {
            setError(language === 'ar' ? 'يرجى إدخال رمز التحقق' : 'Please enter a verification code')
            return
        }

        setLoading(true)
        setError(null)
        setResult(null)

        try {
            const response = await axios.get(`${API_URL}/qr/verify`, {
                params: { code: code.trim().toUpperCase() },
                headers: { Authorization: `Bearer ${token}` }
            })

            if (response.data.success) {
                setResult(response.data.data)
            } else {
                setError(response.data.message || 'Verification failed')
            }
        } catch (err) {
            console.error('Verification error:', err)
            if (err.response?.status === 404) {
                setError(language === 'ar' ? 'رمز التحقق غير صالح' : 'Invalid verification code')
            } else if (err.response?.status === 403) {
                setError(language === 'ar' ? 'ليس لديك صلاحية للتحقق من هذا الطلب' : 'You do not have permission to verify this submission')
            } else {
                setError(err.response?.data?.message || 'Verification failed')
            }
        } finally {
            setLoading(false)
        }
    }

    const handleReset = () => {
        setVerificationCode('')
        setResult(null)
        setError(null)
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'success'
            case 'rejected': return 'error'
            case 'pending': return 'warning'
            default: return 'default'
        }
    }

    const getStatusLabel = (status) => {
        const labels = {
            approved: { en: 'Approved', ar: 'تمت الموافقة' },
            rejected: { en: 'Rejected', ar: 'مرفوض' },
            pending: { en: 'Pending', ar: 'قيد الانتظار' }
        }
        return labels[status]?.[language] || status
    }

    const getFieldLabel = (fieldId, formFields) => {
        if (!formFields) return fieldId
        const field = formFields.find(f => f.id === fieldId)
        if (!field) return fieldId
        return language === 'ar' ? (field.label_ar || field.label_en) : (field.label_en || field.label_ar)
    }

    return (
        <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }} dir={isRTL ? 'rtl' : 'ltr'}>
            <Paper sx={{ p: 4, borderRadius: 3 }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <QrIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                        {language === 'ar' ? 'ماسح رمز التحقق' : 'Verification Scanner'}
                    </Typography>
                    <Typography color="text.secondary">
                        {language === 'ar'
                            ? 'امسح رمز QR أو أدخل رمز التحقق يدوياً للتحقق من الطلب'
                            : 'Scan a QR code or enter the verification code manually to verify a submission'}
                    </Typography>
                </Box>

                {/* Camera Section */}
                {!result && (
                    <Box sx={{ mb: 4 }}>
                        {/* Video and Canvas - always rendered but hidden when not active */}
                        <Box sx={{ position: 'relative', mb: 2, display: cameraActive ? 'block' : 'none' }}>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{
                                    width: '100%',
                                    maxHeight: 400,
                                    borderRadius: 8,
                                    backgroundColor: '#000',
                                    objectFit: 'cover'
                                }}
                            />

                            {/* Debug Status Overlay */}
                            {scanStatus && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: 8,
                                    left: 8,
                                    right: 8,
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    p: 1,
                                    borderRadius: 1,
                                    fontSize: '0.875rem',
                                    textAlign: 'center'
                                }}>
                                    {scanStatus}
                                </Box>
                            )}

                            <Button
                                variant="outlined"
                                color="error"
                                onClick={stopCamera}
                                sx={{ mt: 1 }}
                                fullWidth
                            >
                                {language === 'ar' ? 'إيقاف الكاميرا' : 'Stop Camera'}
                            </Button>
                        </Box>

                        <canvas ref={canvasRef} style={{ display: 'none' }} />

                        {/* Camera button - only show when camera not active */}
                        {!cameraActive && (
                            <Button
                                variant="outlined"
                                startIcon={cameraLoading ? <CircularProgress size={20} /> : <CameraIcon />}
                                onClick={startCamera}
                                disabled={cameraLoading}
                                fullWidth
                                sx={{ mb: 2, py: 1.5 }}
                            >
                                {cameraLoading
                                    ? (language === 'ar' ? 'جاري فتح الكاميرا...' : 'Opening camera...')
                                    : (language === 'ar' ? 'مسح رمز QR بالكاميرا' : 'Scan QR Code with Camera')
                                }
                            </Button>
                        )}

                        {cameraError && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {cameraError}
                            </Alert>
                        )}

                        <Divider sx={{ my: 2 }}>
                            <Typography color="text.secondary" variant="body2">
                                {language === 'ar' ? 'أو أدخل الرمز يدوياً' : 'Or enter code manually'}
                            </Typography>
                        </Divider>

                        {/* Manual Entry */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                label={language === 'ar' ? 'رمز التحقق' : 'Verification Code'}
                                placeholder="FORM-2026-ABC123"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                                onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
                                disabled={loading}
                            />
                            <Button
                                variant="contained"
                                onClick={() => handleVerify()}
                                disabled={loading || !verificationCode.trim()}
                                sx={{ minWidth: 120 }}
                            >
                                {loading ? <CircularProgress size={24} /> : (language === 'ar' ? 'تحقق' : 'Verify')}
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Result */}
                {result && (
                    <Box>
                        <Alert
                            severity="success"
                            icon={<CheckIcon />}
                            sx={{ mb: 3 }}
                        >
                            {language === 'ar' ? 'تم التحقق من الطلب بنجاح' : 'Submission verified successfully'}
                        </Alert>

                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            {language === 'ar' ? 'رقم الطلب' : 'Submission ID'}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={600}>
                                            {result.submission?.submission_id}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            {language === 'ar' ? 'رمز التحقق' : 'Verification Code'}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                            {result.submission?.verification_code}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            {language === 'ar' ? 'النموذج' : 'Form'}
                                        </Typography>
                                        <Typography fontWeight={500}>
                                            {language === 'ar'
                                                ? result.submission?.form_title_ar
                                                : result.submission?.form_title}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            {language === 'ar' ? 'الحالة' : 'Status'}
                                        </Typography>
                                        <Box>
                                            <Chip
                                                label={getStatusLabel(result.submission?.status)}
                                                color={getStatusColor(result.submission?.status)}
                                                size="small"
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            {language === 'ar' ? 'تاريخ الإرسال' : 'Submitted'}
                                        </Typography>
                                        <Typography>
                                            {new Date(result.submission?.created_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            {language === 'ar' ? 'تم التحقق في' : 'Verified at'}
                                        </Typography>
                                        <Typography>
                                            {new Date(result.verified_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Submission Data */}
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                            {language === 'ar' ? 'بيانات الطلب' : 'Submission Data'}
                        </Typography>
                        <Card sx={{ mb: 3 }}>
                            <CardContent>
                                <Grid container spacing={2}>
                                    {result.submission?.data && Object.entries(result.submission.data).map(([key, value]) => (
                                        <Grid item xs={12} sm={6} key={key}>
                                            <Typography variant="caption" color="text.secondary">
                                                {getFieldLabel(key, result.submission?.form_fields)}
                                            </Typography>
                                            <Typography>
                                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Approvals */}
                        {result.approvals?.length > 0 && (
                            <>
                                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                                    {language === 'ar' ? 'سجل الموافقات' : 'Approval History'}
                                </Typography>
                                <Card sx={{ mb: 3 }}>
                                    <CardContent>
                                        {result.approvals.map((approval, index) => (
                                            <Box key={index} sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                mb: index < result.approvals.length - 1 ? 2 : 0
                                            }}>
                                                {approval.status === 'approved' ? (
                                                    <CheckIcon color="success" />
                                                ) : approval.status === 'denied' ? (
                                                    <CancelIcon color="error" />
                                                ) : (
                                                    <CircularProgress size={20} />
                                                )}
                                                <Box>
                                                    <Typography fontWeight={500}>
                                                        {language === 'ar'
                                                            ? approval.department_name_ar
                                                            : approval.department_name}
                                                    </Typography>
                                                    {approval.note && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {approval.note}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </>
                        )}

                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleReset}
                            fullWidth
                        >
                            {language === 'ar' ? 'مسح رمز آخر' : 'Scan Another Code'}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    )
}

export default Scanner
